import { useState, useEffect } from "react";
import { ScreenSizes, useScreenSizes } from "./useScreenSize";

export const Page = ({ classNames, children }) => (
  <article className={["flex flex-col flex-1 ", ...(classNames ?? [])].join(" ")}>
    {children}
  </article>
);
export const CommonPage = ({ classNames, children }) => (
  <Page classNames={["commo", ...(classNames ?? [])]}>{children}</Page>
);
export const Content = ({ classNames, children }) => (
  <section className={["w-full", ...(classNames ?? [])].join(" ")}>{children}</section>
);
export const Panel = ({ isVisible = true, hasPadding = true, classNames, children }) => (
  <div
    className={[
      "bg-primary-option_bg rounded",
      isVisible ? "" : "hidden",
      hasPadding ? "p-7" : "",
      ...(classNames ?? []),
    ].join(" ")}>
    {children}
  </div>
);
export const ActionBar = ({ classNames, children }) => (
  <div className={["ui-action-bar flex flex-row", ...(classNames ?? [])].join(" ")}>{children}</div>
);

export const ErrorMessage = ({ children }) => (
  <div className={["text-red-500 text-center font-bold"].join(" ")}>{children}</div>
);

export const PageTitle = ({ title, classNames, children }) => {
  return (
    <section
      className={[
        "p-4 bg-main_admin_bg_color  rounded text-natural-description_text text-[20px] flex flex-row items-center justify-between",
        ...(classNames ?? []),
      ].join(" ")}>
      {title ? <h1>{title}</h1> : <></>}
      {children ? <div>{children}</div> : <></>}
    </section>
  );
};

const IfHasAccess = ({ isAccessAllowed, fallback = <></>, children }) => {
  return (
    <>
      {
        isAccessAllowed
          ? children
          : fallback
      }
    </>
  )
};

export const IfHasPermissions = ({ permissions, scope, fallback, children }) => {
  const authOptions = useAuth();
  return <IfHasAccess isAccessAllowed={authOptions.authStore.hasPermissions(permissions, scope)} fallback={fallback}>{children}</IfHasAccess>;
};
export const IfHasAnyPermission = ({ permissions, scope, fallback, children }) => {
  const authOptions = useAuth();
  return <IfHasAccess isAccessAllowed={authOptions.authStore.hasAnyPermission(permissions, scope)} fallback={fallback}>{children}</IfHasAccess>;
};

export const ResponsiveGap = ({
  top = true,
  bottom = true,
  left = true,
  right = true,
  classNames,
  children,
}) => {
  const allClassNames = ["p-4 rounded  ", ...(classNames ?? [])];
  if (!top) {
    allClassNames.push("pt-0");
  }
  if (!bottom) {
    allClassNames.push("pb-0");
  }
  if (!left) {
    allClassNames.push("pl-0");
  }
  if (!right) {
    allClassNames.push("pr-0");
  }
  return <div className={allClassNames.join(" ")}>{children}</div>;
};
export const IfScreenSize = ({ sizeRequest, children }) => {
  const screenSize = useScreenSizes();
  if (sizeRequest.length > 0) {
    return sizeRequest.includes(screenSize) ? <>{children}</> : <></>;
  }
  return <>{children}</>;
};

export const IfSmallScreen = ({ children }) => (
  <IfScreenSize sizeRequest={[ScreenSizes.Small]}>{children}</IfScreenSize>
);
export const IfMediumScreen = ({ children }) => (
  <IfScreenSize sizeRequest={[ScreenSizes.Medium]}>{children}</IfScreenSize>
);
export const IfLargeScreen = ({ children }) => (
  <IfScreenSize sizeRequest={[ScreenSizes.Large]}>{children}</IfScreenSize>
);
export const IfAtLeastMediumScreen = ({ children }) => (
  <IfScreenSize sizeRequest={[ScreenSizes.Medium, ScreenSizes.Large]}>{children}</IfScreenSize>
);

export const OpenablePanel = ({
  isOpenedByDefault,
  isOpened,
  hasPadding = true,
  classNames,
  openControlRenderer,
  closeControlRenderer,
  onOpen,
  onClose,
  children,
}) => {
  const [isOpenedState, setIsOpenedState] = useState(
    isOpenedByDefault === undefined ? false : isOpenedByDefault,
  );
  const onOpenHandler = () => {
    // eslint-disable-next-line no-unused-expressions
    onOpen ? onOpen() : null;
    setIsOpenedState(true);
    if (isOpened === undefined) {
      setIsOpenedState(true);
    }
  };
  const onCloseHandler = () => {
    // eslint-disable-next-line no-unused-expressions
    onClose ? onClose() : null;
    if (isOpened === undefined) {
      setIsOpenedState(false);
    }
  };
  return (
    <div
      className={[
        "ui-openable-panel w-full bg-primary-openablepanel_bg ",
        ...(classNames ?? []),
      ].join(" ")}>
      {(isOpened === undefined ? isOpenedState : isOpened) ? (
        <>
          {closeControlRenderer(onCloseHandler)}
          <Panel isVisible={true} hasPadding={hasPadding}>
            {children}
          </Panel>
        </>
      ) : (
        openControlRenderer(onOpenHandler)
      )}
    </div>
  );
};

export const LoadingSpinner = ({ className = "" }) => {
  return (
    <div
      className={`fixed inset-0 z-[9] flex flex-col items-center justify-center bg-white/60 font-sans text-center rounded ${className}`}>
      <img src="/logo_anim.gif" alt="Loading..." className={`w-[10rem] h-[10rem] ${className}`} />
    </div>
  );
};

export const LoadableContent = ({
  isLoading = false,
  loadingView,
  isError,
  errorView,
  children,
}) => {
  useEffect(() => {
    if (isLoading) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isLoading]);

  return (
    <div className="relative h-full w-full">
      {isError ? (errorView ?? <ErrorMessage>Something went wrong while loading this data. Please try again.</ErrorMessage>) : children}
      {isLoading && (loadingView ?? <LoadingSpinner />)}
    </div>
  );
};

export const SubText = ({ classNames, children }) => (
  <p className={["ui-sub-text opacity-50", ...(classNames ?? [])].join(" ")}>{children}</p>
);

export const InRow = ({ noWrap = false, gap = 2, classNames, children }) => {
  const css = {
    gap: `${gap}rem`,
  };
  return (
    <div
      className={[
        "flex flex-row flex-wrap justify-start items-center gap-8  ",
        noWrap ? "flex-nowrap" : "",
        ...(classNames ?? []),
      ].join(" ")}
      style={css}>
      {children}
    </div>
  );
};
